import React from 'react';
import type { ApprovalReportData } from '../services/adminAuth';
import { ApprovalType } from '../services/adminAuth';

interface ReportRecordsTableProps {
    data: ApprovalReportData | null;
    loading: boolean;
    /** When true, renders a checkbox column with select-all in the header. */
    selectable?: boolean;
    selectedRefIds?: Set<string>;
    onToggle?: (referenceId: string) => void;
    onToggleAll?: () => void;
    allSelected?: boolean;
    emptyMessage?: string;
    /** Drives payment-specific rendering (e.g. showing reviewer account numbers). */
    reportType?: number;
}

const namesOf = (arr: { name: string }[] | null | undefined) =>
    (arr && arr.length > 0) ? arr.map(x => x.name).filter(Boolean).join(', ') : '—';

// Comma-separated author names with the lead author(s) listed first.
const authorsText = (authors: { name: string; isLeadAuthor: boolean }[] | null | undefined) => {
    if (!authors || authors.length === 0) return '—';
    const ordered = [...authors].sort((a, b) => (b.isLeadAuthor ? 1 : 0) - (a.isLeadAuthor ? 1 : 0));
    return ordered.map(a => a.name).filter(Boolean).join(', ') || '—';
};

/** Tabular list of report/batch records, optionally selectable for the picker flows. */
const ReportRecordsTable: React.FC<ReportRecordsTableProps> = ({
    data,
    loading,
    selectable = false,
    selectedRefIds,
    onToggle,
    onToggleAll,
    allSelected = false,
    emptyMessage = 'No records found.',
    reportType,
}) => {
    const isReviewerPayment = reportType === ApprovalType.ReviewerPayments;

    if (loading) {
        return (
            <div className="py-[60px] flex justify-center items-center">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin" />
            </div>
        );
    }

    const details = data?.approvalRequestDetail ?? [];
    if (details.length === 0) {
        return <div className="py-[60px] text-center text-[#6b7280] text-sm sm:text-base">{emptyMessage}</div>;
    }

    const th = 'py-[12px] px-[8px] text-left text-[12px] sm:text-[14px] font-[700] text-[#0A0A0A] border-b border-[#0000001A]';
    const td = 'py-[10px] px-[8px] text-[12px] sm:text-[14px] border-t border-[#0000001A]';

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
                <thead>
                    <tr>
                        {selectable && (
                            <th className={`${th} w-[40px]`}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={onToggleAll}
                                    className="w-4 h-4 accent-green-600 cursor-pointer"
                                    title="Select all"
                                />
                            </th>
                        )}
                        <th className={`${th} w-[48px]`}>S/N</th>
                        <th className={`${th} whitespace-nowrap`}>Sub. No</th>
                        <th className={th}>Title</th>
                        <th className={th}>Authors</th>
                        <th className={th}>Reviewers</th>
                        <th className={th}>Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {details.map((item, i) => {
                        const checked = selectable && !!selectedRefIds?.has(item.referenceId);
                        return (
                            <tr
                                key={item.referenceId || i}
                                className={`${selectable ? 'cursor-pointer' : ''} hover:bg-[#f9fafb] ${checked ? 'bg-green-50/40' : ''}`}
                                onClick={selectable ? () => onToggle?.(item.referenceId) : undefined}
                            >
                                {selectable && (
                                    <td className={`${td} text-[#111827]`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => onToggle?.(item.referenceId)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 accent-green-600 cursor-pointer"
                                        />
                                    </td>
                                )}
                                <td className={`${td} text-gray-700`}>{i + 1}</td>
                                <td className={`${td} text-[#111827] whitespace-nowrap`}>{item.submissionNumber || '—'}</td>
                                <td className={`${td} text-[#111827] font-medium`}>{item.title || '—'}</td>
                                <td className={`${td} text-[#111827]`}>{authorsText(item.authorDetails)}</td>
                                <td className={`${td} text-gray-600`}>
                                    {isReviewerPayment
                                        ? (item.reviewerDetails && item.reviewerDetails.length > 0
                                            ? <div className="space-y-1.5">
                                                {item.reviewerDetails.map((r, ri) => (
                                                    <div key={r.id || ri}>
                                                        <div className="text-gray-800">{r.name || '—'}</div>
                                                        {r.accountNumber && <div className="text-[11px] text-gray-500">Acct: {r.accountNumber}</div>}
                                                    </div>
                                                ))}
                                            </div>
                                            : '—')
                                        : namesOf(item.reviewerDetails)}
                                </td>
                                <td className={`${td} text-gray-500`}>{item.remarks || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ReportRecordsTable;
